import { useReducer } from 'react';
import { PrimaryButton } from 'ui/button';
import { AddNew16 } from 'ui/icons/aurora/16px';
import { Tooltip } from 'ui/tooltip';
import { PageHeader } from 'ui/pageHeader';
import {
  CREATE_NEW_COLLECTION_TEXT,
  CATALOG_COLLECTIONS_TITLE,
  MISSING_MANAGE_CATALOGS_PERMISSION,
} from 'modules/catalog/catalogConstants';
import CreateCollectionModal from 'modules/catalog/createCollectionModal';
import CollectionsHandlebarsModal from 'modules/catalog/collectionsHandlebarsModal';
import NavigationSidebar from 'modules/catalog/navigationSidebar';
import { DeleteCollectionModal } from './DeleteCollectionModal';
import CollectionsTable from './CollectionsTable';
import { CollectionsContainer } from './Collections.styled';
import { SET_MODAL_STATE, INITIAL_REDUCER_STATE } from '../constants';

// type Props = {
//   /** Whether user has RBAC permission to manage catalogs */
//   canManageCatalogs: Boolean,
//   /**
//    * Function to change the details of the current collection
//    * ```
//    * changeCollectionDetails(selectedCollection)
//    * ```
//    * @param { Object } selectedCollection: The selected collection.
//    * @returns { void }
//    */
//   changeCollectionDetails: (selectedCollection: {}) => {},
//   /**
//    * Function to close delete modal.
//    * ```
//    * closeDeleteModal()
//    * ```
//    * @returns { void }
//    */
//   closeDeleteModal: () => void,
//   /** The list of collections to show in the table. */
//   collections: [],
//   /**
//    * Function to delete selected catalog collection.
//    * ```
//    * deleteCatalogCollection()
//    * ```
//    * @returns { void }
//    */
//   deleteCatalogCollection: () => void,
//   /**
//    * Function to fetch catalog collections.
//    * ```
//    * fetchCatalogCollections(fetchParams)
//    * ```
//    * @param { Object } fetchParams: Additional params to fetch collections (e.g. Page, PageSize...)
//    * @returns { void }
//    */
//   fetchCatalogCollections: (fetchParams: {}) => void,
//   /** The fetch params for the catalog queries to use with the table. (e.g. Page, PageSize...) */
//   fetchParams: {},
//   /** If the delete modal is open. */
//   isDeleteModalOpen: boolean,
//   /** If currently fetching collections. Toggles loading spinner. */
//   isFetchingCollections: boolean,
//   /**
//    * Function to open delete modal.
//    * ```
//    * openDeleteModal(selectedCollection)
//    * ```
//    * @param { Object } selectedCollection: The collection selected to be deleted.
//    * @returns { void }
//    */
//   openDeleteModal: () => void,
//   /**
//    * Function to open handlebars modal.
//    * ```
//    * openHandlebarsModal(selectedCollectionName)
//    * ```
//    * @param { String } selectedCollectionName: The selected collection name.
//    * @returns { void }
//    */
//   openHandlebarsModal: (selectedCollectionName: string) => void,
//   /**
//    * Function to set handlebar snippet.
//    * ```
//    * setUsesSmartRecosHandlebar(usesSmartRecos)
//    * ```
//    * @param { String } usesSmartRecos: Whether collection uses product recommendations.
//    * @returns { void }
//    */
//   setUsesSmartRecosHandlebar: (usesSmartRecos: boolean) => void,
//   /** The currently selected query. Marked for edit/delete. */
//   selectedCollection: {},
//   /** Store object to get state. (Used for connected CreateCollectionModal component) */
//   store: {},
//   /** The total number of catalog collections. */
//   total: number,
//   /** Indicates if the ProductRecommendations feature flag is on */
//   isProductRecommendationsOn: Boolean,
// };

const CreateCollectionModalReducer = (state, action) => {
  switch (action.type) {
    case SET_MODAL_STATE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const Collections = ({
  canManageCatalogs,
  changeCollectionDetails,
  closeDeleteModal,
  collections,
  deleteCatalogCollection,
  fetchCatalogCollections,
  fetchParams,
  isDeleteModalOpen,
  isFetchingCollections,
  openDeleteModal,
  openHandlebarsModal,
  selectedCollection,
  total,
  store,
  isProductRecommendationsOn,
  setUsesSmartRecosHandlebar,
}) => {
  const [modalState, updateModalState] = useReducer(
    CreateCollectionModalReducer,
    INITIAL_REDUCER_STATE,
  );

  const toolbarContent = (
    <>
      <CreateCollectionModal
        isOpen={modalState.isOpen}
        isEdit={modalState.isEditModal}
        collection={modalState.collection}
        closeModal={() =>
          updateModalState({
            type: SET_MODAL_STATE,
            payload: { isOpen: false, isEditModal: false, collection: null },
          })
        }
        shouldFetchCatalogs
      />
      <Tooltip isEnabled={!canManageCatalogs} tooltipContent={MISSING_MANAGE_CATALOGS_PERMISSION}>
        <PrimaryButton
          data-test="collections-create-collection-button"
          isDisabled={!canManageCatalogs}
          onClick={() =>
            updateModalState({
              type: SET_MODAL_STATE,
              payload: { isOpen: true, isEditModal: false, collection: null },
            })
          }
        >
          <AddNew16 />
          {CREATE_NEW_COLLECTION_TEXT}
        </PrimaryButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <PageHeader>{CATALOG_COLLECTIONS_TITLE}</PageHeader>
      <CollectionsContainer>
        <NavigationSidebar />
        <CollectionsTable
          canManageCatalogs={canManageCatalogs}
          changeCollectionDetails={changeCollectionDetails}
          fetchParams={fetchParams}
          isLoading={isFetchingCollections}
          onConfirmDeleteCollection={openDeleteModal}
          onEditCollection={(collection) =>
            updateModalState({
              type: SET_MODAL_STATE,
              payload: { isOpen: true, isEditModal: true, collection },
            })
          }
          onFetchCollections={fetchCatalogCollections}
          openHandlebarsModal={openHandlebarsModal}
          setUsesSmartRecosHandlebar={setUsesSmartRecosHandlebar}
          collections={collections}
          toolbarContent={toolbarContent}
          total={total}
          isProductRecommendationsOn={isProductRecommendationsOn}
        />
      </CollectionsContainer>
      {isDeleteModalOpen && (
        <DeleteCollectionModal
          onClose={closeDeleteModal}
          onDelete={deleteCatalogCollection}
          selectedCollection={selectedCollection}
        />
      )}
      <CollectionsHandlebarsModal store={store} />
    </>
  );
};

export default Collections;
